import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Divider, IconButton, Typography, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Skeleton,
  TextField, MenuItem, Grid, FormControl, InputLabel, Select, Checkbox, FormControlLabel, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon, History as HistoryIcon } from '@mui/icons-material';
import { useState, useMemo } from 'react';
import { useAMC } from '@/context/AMCContext';
import MerchantConfigModal from '../components/MerchantConfigModal';
import { TRTYPE_LOGOS } from '@/constants/icons';
import { PaymentConfig, useGetPaymentConfigsByAssetQuery, useCreatePaymentConfigMutation, useDeletePaymentConfigMutation, PaymentMode, TransactionType, AuthMode, useConvertPaymentRulesMutation } from '@/services/paymentConfigApi';
import { useGetMerchantConfigsByAMCQuery } from '@/services/gatewayConfigApi';

export default function PaymentConfigList() {
  const { amcId, amc } = useAMC();
  const { assetId = '' } = useParams<{ assetId: string }>();
  const navigate = useNavigate();

  const { data: paymentConfigs, isLoading, refetch } = useGetPaymentConfigsByAssetQuery(assetId);
  const { data: merchantConfigs } = useGetMerchantConfigsByAMCQuery(amcId!, { skip: !amcId });
  const [createPaymentConfig] = useCreatePaymentConfigMutation();
  const [deletePaymentConfig] = useDeletePaymentConfigMutation();
  const [convertPaymentRules, { isLoading: isConverting }] = useConvertPaymentRulesMutation();

  const [selectedConfig, setSelectedConfig] = useState<PaymentConfig | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isMerchantConfigOpen, setIsMerchantConfigOpen] = useState(false);

  // migration Dynamic Routing related state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [convertExisting, setConvertExisting] = useState(true);

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<PaymentConfig | null>(null);

  // Add Payment Config form state
  const [addFormData, setAddFormData] = useState({
    trtype: '',
    paymode: '',
    authmode: '',
    fundCode: '',
  });

  const [selectedMerchantConfigs, setSelectedMerchantConfigs] = useState<any[]>([]);
  const [selectedFundCode, setSelectedFundCode] = useState<string>('all');

  const handleEditClick = (config: PaymentConfig) => {
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedConfig(null);
    refetch(); // Reload the data when modal closes
  };

  const handleAddClick = () => {
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    setAddFormData({
      trtype: '',
      paymode: '',
      authmode: '',
      fundCode: '',
    });
    setSelectedMerchantConfigs([]);
  };

  const handleMerchantConfigClick = () => {
    setIsMerchantConfigOpen(true);
  };

  const handleCloseMerchantConfig = () => {
    setIsMerchantConfigOpen(false);
  };

  const handleCreatePaymentConfig = async () => {
    try {
      const newConfig = {
        name: `${addFormData.trtype}_${addFormData.paymode}_${addFormData.authmode}`,
        trtype: addFormData.trtype as TransactionType,
        paymode: addFormData.paymode as PaymentMode,
        authmode: addFormData.authmode as AuthMode,
        fundCode: addFormData.fundCode,
        asset: { id: assetId },
        merchantConfigs: selectedMerchantConfigs,
        gatewaySelectionAlgo: 'Percentage' as const,
      };

      await createPaymentConfig(newConfig).unwrap();
      handleCloseAddDialog();
      refetch();
    } catch (error) {
      console.error('Failed to create payment config:', error);
    }
  };

  const handleConfirmMigration = async () => {
    try {
      setConfirmOpen(false);
      if (convertExisting && assetId) {
        await convertPaymentRules(assetId).unwrap();
      }
      setConvertExisting(false);
      navigate(`/amcs/${amcId}/assets/${assetId}/try-dynamic-routing`);
    } catch (err) {
      console.error('Migration failed', err);
    }
  };

  const handleDeleteClick = (config: PaymentConfig) => {
    setConfigToDelete(config);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;

    try {
      await deletePaymentConfig(configToDelete.id).unwrap();
      setDeleteConfirmOpen(false);
      setConfigToDelete(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete payment config:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setConfigToDelete(null);
  };

  // Extract fund codes that are currently used in payment configs (for the filter dropdown)
  const usedFundCodes = useMemo(() => {
    if (!paymentConfigs || !merchantConfigs) return [];
    const fundCodes = new Set<string>();

    // Get the fund codes that are actually used in payment configs
    const merchantFundCodeMap = new Map(
      merchantConfigs.map((mc: any) => [mc.id, mc.fundCode])
    );

    paymentConfigs.forEach(config => {
      config.merchantConfigs.forEach(mc => {
        const fundCode = merchantFundCodeMap.get(mc.id);
        if (fundCode) {
          fundCodes.add(fundCode);
        }
      });
    });

    return Array.from(fundCodes).sort();
  }, [paymentConfigs, merchantConfigs]);

  // Extract ALL fund codes from merchant configs (for the Add dialog)
  const allAvailableFundCodes = useMemo(() => {
    if (!merchantConfigs) return [];
    const fundCodes = new Set<string>();

    // Get all unique fund codes from merchant configs
    merchantConfigs.forEach((mc: any) => {
      if (mc.fundCode) {
        fundCodes.add(mc.fundCode);
      }
    });

    return Array.from(fundCodes).sort();
  }, [merchantConfigs]);

  // Filter payment configs based on selected fund code
  const filteredPaymentConfigs = useMemo(() => {
    if (!paymentConfigs || !merchantConfigs) return paymentConfigs || [];
    if (selectedFundCode === 'all') return paymentConfigs;

    // Create a map of merchant config ID to fund code from the merchant API
    const merchantFundCodeMap = new Map(
      merchantConfigs.map((mc: any) => [mc.id, mc.fundCode])
    );

    // Filter payment configs that have merchant configs with the selected fund code
    return paymentConfigs.filter(config =>
      config.merchantConfigs.some(mc => {
        const fundCode = merchantFundCodeMap.get(mc.id);
        return fundCode === selectedFundCode;
      })
    ).map(config => ({
      ...config,
      // Only show merchant configs that match the selected fund code
      merchantConfigs: config.merchantConfigs.filter(mc =>
        merchantFundCodeMap.get(mc.id) === selectedFundCode
      )
    }));
  }, [paymentConfigs, merchantConfigs, selectedFundCode]);

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box px={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Payment Configurations</Typography>
        <Box display="flex" gap={2} alignItems="center">
          {amc?.isRTA && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Fund Code</InputLabel>
              <Select
                value={selectedFundCode}
                label="Fund Code"
                onChange={(e) => setSelectedFundCode(e.target.value)}
              >
                <MenuItem value="all">All Funds</MenuItem>
                {usedFundCodes.length === 0 ? (
                  <MenuItem disabled value="">
                    No fund codes available
                  </MenuItem>
                ) : (
                  usedFundCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}
          {amc?.isRTA && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              sx={{
                textTransform: 'none',
                fontWeight: '600',
              }}
            >
              Add
            </Button>
          )}
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => {
              // Navigate to the static payment configs history page
              navigate(`/amcs/${amcId}/assets/${assetId}/payment-configs/history`);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: '600',
            }}
          >
            Config History
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setConfirmOpen(true)}
            sx={{
              textTransform: 'none',
              fontWeight: '600',
            }}
          >
            Try Dynamic Routing
          </Button>
        </Box>
      </Box>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Transaction Type</TableCell>
              <TableCell>Payment Mode</TableCell>
              <TableCell>Auth Mode</TableCell>
              <TableCell>Merchant Configs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="60%" />
                  </TableCell>
                </TableRow>
              ))
              : filteredPaymentConfigs?.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={TRTYPE_LOGOS[config.trtype as keyof typeof TRTYPE_LOGOS]}
                        alt={config.trtype}
                        style={{ width: 24, height: 24 }}
                      />
                      {config.trtype}
                    </Box>
                  </TableCell>
                  <TableCell>{config.paymode}</TableCell>
                  <TableCell>{config.authmode}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" sx={{ height: '100%' }}>
                      {config.merchantConfigs.length === 0
                        ? 'None'
                        : config.merchantConfigs
                          // .sort(
                          //   (a, b) => (b?.frequency || a?.priority || 0) - (a?.frequency || b?.priority || 1)
                          // )
                          .map((mc) => (
                            <Box
                              key={mc.aggregator}
                              sx={{ display: 'flex', gap: 1, mx: 0.5, alignItems: 'center' }}
                            >
                              <Box>{mc.aggregator}</Box>
                              <Typography
                                variant="body2"
                                color="primary"
                                sx={(theme) => ({
                                  fontWeight: '600',
                                  width: 56,
                                  py: 0.7,
                                  backgroundColor: theme.palette.background.default,
                                  borderRadius: 1,
                                  textAlign: 'center',
                                })}
                              >
                                {(mc as any).gatewaySelectionAlgo === 'Percentage' ||
                                  !(mc as any).gatewaySelectionAlgo
                                  ? `${mc.frequency}%`
                                  : `P${mc.priority}`}
                              </Typography>
                            </Box>
                          ))}
                      <Divider orientation="vertical" flexItem sx={{ ml: 1, my: 1 }} />
                      <IconButton
                        sx={{ ml: 1 }}
                        color="primary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(config);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        sx={{ ml: 1 }}
                        color="error"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(config);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedConfig && (
        <MerchantConfigModal
          open={isModalOpen}
          onClose={handleCloseModal}
          gatewaySelectionAlgo={selectedConfig.gatewaySelectionAlgo || 'Percentage'}
          paymentConfigId={selectedConfig.id}
          amcId={amcId || ''}
          initialConfigs={selectedConfig.merchantConfigs}
        />
      )}

      {/* Add Payment Configuration Dialog */}
      <Dialog open={isAddDialogOpen} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle>Add Payment Configuration</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="TrType"
                value={addFormData.trtype}
                onChange={(e) => setAddFormData(prev => ({ ...prev, trtype: e.target.value }))}
                size="small"
              >
                <MenuItem value={TransactionType.LUMPSUM}>{TransactionType.LUMPSUM}</MenuItem>
                <MenuItem value={TransactionType.EMANDATE}>{TransactionType.EMANDATE}</MenuItem>
                <MenuItem value={TransactionType.HYBRID}>{TransactionType.HYBRID}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Pay Mode"
                value={addFormData.paymode}
                onChange={(e) => setAddFormData(prev => ({ ...prev, paymode: e.target.value }))}
                size="small"
              >
                <MenuItem value={PaymentMode.NET_BANKING}>{PaymentMode.NET_BANKING}</MenuItem>
                <MenuItem value={PaymentMode.UPI}>{PaymentMode.UPI}</MenuItem>
                <MenuItem value={PaymentMode.DEBIT_CARD}>{PaymentMode.DEBIT_CARD}</MenuItem>
                <MenuItem value={PaymentMode.ENACH}>{PaymentMode.ENACH}</MenuItem>
                <MenuItem value={PaymentMode.UPI_AUTOPAY}>{PaymentMode.UPI_AUTOPAY}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Auth Mode"
                value={addFormData.authmode}
                onChange={(e) => setAddFormData(prev => ({ ...prev, authmode: e.target.value }))}
                size="small"
              >
                <MenuItem value={AuthMode.NET_BANKING}>{AuthMode.NET_BANKING}</MenuItem>
                <MenuItem value={AuthMode.DEBIT_CARD}>{AuthMode.DEBIT_CARD}</MenuItem>
                <MenuItem value={AuthMode.UPI}>{AuthMode.UPI}</MenuItem>
                <MenuItem value={AuthMode.AADHAR}>{AuthMode.AADHAR}</MenuItem>
                <MenuItem value={AuthMode.PAN}>{AuthMode.PAN}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Fund Code"
                value={addFormData.fundCode}
                onChange={(e) => setAddFormData(prev => ({ ...prev, fundCode: e.target.value }))}
                size="small"
              >
                {allAvailableFundCodes.length === 0 ? (
                  <MenuItem disabled value="">
                    No fund codes available
                  </MenuItem>
                ) : (
                  allAvailableFundCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleMerchantConfigClick}
                sx={{ mt: 2 }}
              >
                Manage Merchant Configuration
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button
            onClick={handleCreatePaymentConfig}
            variant="contained"
            disabled={!addFormData.trtype || !addFormData.paymode || !addFormData.authmode}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merchant Configuration Modal */}
      <MerchantConfigModal
        open={isMerchantConfigOpen}
        onClose={handleCloseMerchantConfig}
        gatewaySelectionAlgo="Percentage"
        paymentConfigId=""
        amcId={amcId || ''}
        initialConfigs={selectedMerchantConfigs}
        onConfigsChange={setSelectedMerchantConfigs}
        filterByFundCode={addFormData.fundCode}
      />

      {/* Confirm migration dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Try out Upgraded Routing</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You can try out new and improved routing. Don't worry, you can decide to continue using dynamic routing or come back to this screen if you are not ready yet. <br />
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={convertExisting}
                onChange={(e) => setConvertExisting(e.target.checked)}
              />
            }
            label="Convert existing rules to dynamic rules for this AMC"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmOpen(false); setConvertExisting(false); }}>Cancel</Button>
          <Button
            onClick={handleConfirmMigration}
            color="secondary"
            variant="contained"
            disabled={isConverting}
          >
            {isConverting ? 'Converting...' : 'Confirm & Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Payment Configuration</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this payment configuration? This action cannot be undone.
            <br />
            <br />
            <strong>Configuration:</strong> {configToDelete?.name}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
